import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderOpen, 
  Calendar, 
  User, 
  ArrowRight, 
  Clock,
  CheckCircle,
  AlertTriangle,
  Eye,
  FileText,
  TrendingUp,
  Maximize2
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useProjectStore } from '../../store/projectStore';
import { useAuthStore } from '../../store/authStore';
import { ProjectStatusTimeline } from '../Projects/ProjectStatusTimeline';
import type { Project, ProjectTimeline, ProfileType } from '../../types';

export const LastProjectViewer: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    lastViewedProject, 
    fetchProject, 
    fetchProjectTimeline, 
    setLastViewedProject 
  } = useProjectStore();
  
  const [projectTimeline, setProjectTimeline] = useState<ProjectTimeline | null>(null);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load last viewed project from localStorage on mount
  useEffect(() => {
    if (!lastViewedProject && user?.profile_type === 'PORTEUR') {
      const saved = localStorage.getItem('lastViewedProject');
      if (saved) {
        try {
          const project = JSON.parse(saved);
          setLastViewedProject(project);
        } catch (error) {
          console.error('Error parsing saved project:', error);
          localStorage.removeItem('lastViewedProject');
        }
      }
    }
  }, [lastViewedProject, setLastViewedProject]);

  // Fetch timeline when project changes
  useEffect(() => {
    if (lastViewedProject && user?.profile_type === 'PORTEUR') {
      setIsLoading(true);
      fetchProjectTimeline(lastViewedProject.id)
        .then(setProjectTimeline)
        .finally(() => setIsLoading(false));
    }
  }, [lastViewedProject, fetchProjectTimeline]);

  if (!lastViewedProject || user?.profile_type !== 'PORTEUR' || !user) {
    return null;
  }

  const getStatusColor = (status: string) => {
    const colors = {
      A_PREPARER: 'bg-gray-100 text-gray-800 border-gray-200',
      SOUMIS: 'bg-blue-100 text-blue-800 border-blue-200',
      ACCORD_PROVISOIRE: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      VALIDE: 'bg-green-100 text-green-800 border-green-200',
      DEBLOCAGE_FONDS: 'bg-purple-100 text-purple-800 border-purple-200',
      EN_COURS: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      VERIFICATION: 'bg-orange-100 text-orange-800 border-orange-200',
      TERMINE: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      REJETE: 'bg-red-100 text-red-800 border-red-200',
      SUSPENDU: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      A_PREPARER: 'À préparer',
      SOUMIS: 'Soumis',
      ACCORD_PROVISOIRE: 'Accord provisoire',
      VALIDE: 'Validé',
      DEBLOCAGE_FONDS: 'Déblocage fonds',
      EN_COURS: 'En cours',
      VERIFICATION: 'Vérification',
      TERMINE: 'Terminé',
      REJETE: 'Rejeté',
      SUSPENDU: 'Suspendu'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getDomainLabel = (domain: string) => {
    const labels = {
      AGRICULTURE: 'Agriculture',
      ARTISANAT: 'Artisanat',
      COMMERCE: 'Commerce',
      SERVICES: 'Services',
      INDUSTRIE: 'Industrie',
      TOURISME: 'Tourisme',
      CULTURE: 'Culture',
      FORMATION: 'Formation',
      SOCIAL: 'Social',
      ENVIRONNEMENT: 'Environnement'
    };
    return labels[domain as keyof typeof labels] || domain;
  };

  const getProgressPercentage = () => {
    if (!projectTimeline) return 0;
    
    const totalSteps = 8; // Total steps in project lifecycle
    const currentStepIndex = [
      'A_PREPARER', 'SOUMIS', 'ACCORD_PROVISOIRE', 'VALIDE', 
      'DEBLOCAGE_FONDS', 'EN_COURS', 'VERIFICATION_FINALE', 'TERMINE'
    ].indexOf(projectTimeline.current_status);
    
    return currentStepIndex >= 0 ? Math.round(((currentStepIndex + 1) / totalSteps) * 100) : 0;
  };

  const progressPercentage = getProgressPercentage();

  return (
    <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700/50 rounded-2xl shadow-xl border border-white/60 dark:border-gray-700/60 backdrop-blur-md overflow-hidden animate-slide-up">
      {/* Header with Gradient */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16" />
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12" />
        </div>
        
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-lg">
                <FolderOpen className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">Mon Projet Actuel</h2>
                <p className="text-blue-100 text-sm">Dernière consultation</p>
              </div>
            </div>
            
            <button
              onClick={() => navigate(`/projects/${lastViewedProject.id}`)}
              className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Eye className="w-4 h-4" />
              <span className="text-sm font-medium">Voir détails</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Project Info */}
          <div className="space-y-3">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2 animate-slide-down">
                {lastViewedProject.name}
              </h3>
              <div className="flex items-center space-x-4 animate-slide-down" style={{ animationDelay: '0.1s' }}>
                <span className={`px-3 py-1 rounded-full text-sm font-medium border backdrop-blur-sm ${getStatusColor(lastViewedProject.status)} bg-white/90`}>
                  {getStatusLabel(lastViewedProject.status)}
                </span>
                <span className="text-blue-100 text-sm">
                  {getDomainLabel(lastViewedProject.domain)}
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="animate-slide-down" style={{ animationDelay: '0.2s' }}>
              <div className="flex justify-between text-sm text-blue-100 mb-2">
                <span>Progression globale</span>
                <span>{progressPercentage}%</span>
              </div>
              <div className="w-full bg-white/20 backdrop-blur-sm rounded-full h-3 overflow-hidden shadow-inner">
                <div 
                  className="bg-gradient-to-r from-white via-blue-100 to-white h-3 rounded-full transition-all duration-1000 ease-out shadow-sm"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-100 dark:border-blue-800 hover:shadow-lg transition-all duration-300 hover:scale-105">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {lastViewedProject.documents?.length || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Documents</div>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl border border-green-100 dark:border-green-800 hover:shadow-lg transition-all duration-300 hover:scale-105">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {lastViewedProject.steps?.filter(s => s.status === 'TERMINE').length || 0}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Étapes</div>
          </div>
          
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl border border-purple-100 dark:border-purple-800 hover:shadow-lg transition-all duration-300 hover:scale-105">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {format(new Date(lastViewedProject.updated_at), 'dd/MM', { locale: fr })}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">Dernière MAJ</div>
          </div>
        </div>

        {/* Project Details */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50/50 dark:from-gray-700/50 dark:to-gray-600/50 rounded-xl p-4 border border-gray-100 dark:border-gray-600 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              {lastViewedProject.assigned_advisor && (
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Accompagnateur</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {lastViewedProject.assigned_advisor.full_name || lastViewedProject.assigned_advisor.email}
                    </p>
                  </div>
                </div>
              )}
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                  <Calendar className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Créé le</p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {format(new Date(lastViewedProject.created_at), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
              </div>
            </div>

            {lastViewedProject.funding_requested && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-600 to-red-600 rounded-full flex items-center justify-center shadow-md">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Financement demandé</p>
                    <p className="font-bold text-gray-900 dark:text-gray-100">
                      {lastViewedProject.funding_requested.toLocaleString('fr-FR')} €
                    </p>
                  </div>
                </div>
                
                {lastViewedProject.funding_amount && (
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-600 rounded-full flex items-center justify-center shadow-md">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Financement accordé</p>
                      <p className="font-bold text-green-600 dark:text-green-400">
                        {lastViewedProject.funding_amount.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Timeline Section */}
        <div className="animate-slide-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <span>Frise d'avancement</span>
            </h3>
            <button
              onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
              className="flex items-center space-x-1 px-3 py-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 hover:scale-105"
            >
              <Maximize2 className="w-4 h-4" />
              <span className="text-sm">{isTimelineExpanded ? 'Réduire' : 'Agrandir'}</span>
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : projectTimeline ? (
            <div className={`transition-all duration-500 ${isTimelineExpanded ? 'max-h-none' : 'max-h-64 overflow-hidden'}`}>
              <ProjectStatusTimeline
                currentStatus={projectTimeline.current_status}
                stages={projectTimeline.stages}
                fullscreen={isTimelineExpanded}
                onToggleFullscreen={() => setIsTimelineExpanded(!isTimelineExpanded)}
              />
            </div>
          ) : (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 text-center">
              <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 dark:text-gray-400">Impossible de charger la frise d'avancement</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600 animate-slide-up" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(`/projects/${lastViewedProject.id}/documents`)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
            >
              <FileText className="w-4 h-4" />
              <span className="text-sm font-medium">Gérer documents</span>
            </button>
            
            <button
              onClick={() => navigate('/rdv')}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 hover:scale-105 shadow-md hover:shadow-lg"
            >
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Prendre RDV</span>
            </button>
          </div>

          <button
            onClick={() => navigate('/projects')}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-all duration-200 hover:scale-105"
          >
            <span className="text-sm">Voir tous mes projets</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};